import {useEffect, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {canViewTenantUsers, useUser} from "../providers/user-context";
import {getCourierTracking} from "./courier-api";

export default function useCourierTracking({courierId, status, page}) {
    const {tenantId, tenantType, userRole, userIdentity, userFullName, userLoading, userError} = useUser();
    const {i18n: {language}} = useTranslation();
    const allowed = !!tenantId && !userLoading && !userError && canViewTenantUsers(tenantType, userRole);
    const identity = JSON.stringify([tenantId, userIdentity, userFullName, userRole, allowed]);
    const scope = JSON.stringify([identity, language, courierId, status, page]);
    const [result, setResult] = useState({scope: null, data: null, loading: false, error: ""});
    const refresh = useRef(() => {});

    // Keep loading and timer cleanup together so each request shares the same scope fence.
    useEffect(() => {
        if (!allowed) return;
        const controller = new AbortController();
        let pending = false;
        const load = async () => {
            if (pending || controller.signal.aborted || document.visibilityState === "hidden") return;
            pending = true;
            setResult(previous => ({scope, loading: true, error: "", data: previous.scope === scope ? previous.data : null}));
            try {
                const data = await getCourierTracking({courierId: courierId || undefined, page, size: 20}, language, controller.signal);
                if (controller.signal.aborted) return;
                if (!Array.isArray(data.content) || !Number.isInteger(data.totalPages)
                    || data.content.some(item => !item.courierId || (courierId && item.courierId !== courierId))) {
                    setResult({scope, loading: false, data: null, error: "CourierMapLoadFailed"});
                    return;
                }
                setResult({scope, loading: false, data, error: ""});
            } catch (error) {
                if (!controller.signal.aborted) setResult(previous => ({scope, loading: false,
                    data: previous.scope === scope && (!error.response || error.response.status >= 500) ? previous.data : null,
                    error: "CourierMapLoadFailed"}));
            } finally { pending = false; }
        };
        refresh.current = load;
        load();
        const interval = setInterval(load, 15000);
        document.addEventListener("visibilitychange", load);
        window.addEventListener("focus", load);
        return () => {
            controller.abort(); clearInterval(interval); refresh.current = () => {};
            document.removeEventListener("visibilitychange", load); window.removeEventListener("focus", load);
        };
    }, [allowed, tenantId, courierId, page, language, scope]);

    return {...(allowed && result.scope === scope ? result : {data: null, loading: allowed, error: ""}),
        identity, allowed, refresh: () => refresh.current()};
}
